import React from 'react';
import { parseCalendarDate } from '../../helper/helper';

const DAY_LETTERS = ['D', 'L', 'Ma', 'Me', 'J', 'V', 'S'];

const formatScheduleCompact = (schedule) => {
  if (!Array.isArray(schedule) || schedule.length === 0) return null;
  const monthList = [
    'janv', 'févr', 'mars', 'avr', 'mai', 'juin',
    'juil', 'août', 'sept', 'oct', 'nov', 'déc',
  ];
  return schedule.map((week) => {
    const days = [...(week.days || [])].sort(
      (a, b) => parseCalendarDate(a.date) - parseCalendarDate(b.date)
    );
    if (days.length === 0) return { name: week.name || 'Semaine', items: [] };
    const firstDate = parseCalendarDate(days[0].date);
    const lastDate = parseCalendarDate(days[days.length - 1].date);
    const range =
      firstDate.getTime() === lastDate.getTime()
        ? `${firstDate.getDate()} ${monthList[firstDate.getMonth()]}`
        : `${firstDate.getDate()}-${lastDate.getDate()} ${monthList[lastDate.getMonth()]}`;
    const items = days.map((d) => {
      const dt = parseCalendarDate(d.date);
      const dayLetter = DAY_LETTERS[dt.getDay()];
      const p = d.periods;
      if (!p || Object.keys(p).length === 0)
        return { day: dayLetter, label: '—' };
      if (p.periode_one_start != null && p.periode_one_end != null) {
        if (
          p.periode_one_start === '00:00' &&
          p.periode_one_end === '00:00'
        ) {
          return { day: dayLetter, label: 'Non travaillé' };
        }
        let label = `${p.periode_one_start}–${p.periode_one_end}`;
        if (p.periode_two_start && p.periode_two_end) {
          label += ` / ${p.periode_two_start}–${p.periode_two_end}`;
        }
        if (p.periode_three_start && p.periode_three_end) {
          label += ` / ${p.periode_three_start}–${p.periode_three_end}`;
        }
        return { day: dayLetter, label };
      }
      return { day: dayLetter, label: 'Non renseigné' };
    });
    return { name: week.name || 'Semaine', range, items };
  });
};

const ScheduleCompactBlock = ({ data }) => {
  if (!data || data.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {data.map((week, wi) => (
        <div
          key={wi}
          className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100"
        >
          <div className="font-medium text-gray-700 text-[11px]">
            {week.name}
            {week.range && (
              <span className="text-gray-500 font-normal ml-1">
                ({week.range})
              </span>
            )}
          </div>
          {week.items && week.items.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {week.items.map((item, di) => (
                <span
                  key={di}
                  className="inline-flex items-center gap-0.5 bg-white border border-gray-100 rounded px-1.5 py-0.5 text-[10px]"
                >
                  <span className="text-gray-500 font-medium w-5">
                    {item.day}
                  </span>
                  <span className="text-gray-700">{item.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const ScheduleChangeBeforeAfter = ({ before, after }) => {
  const beforeSchedule =
    Array.isArray(before) ? formatScheduleCompact(before) : null;
  const afterSchedule =
    Array.isArray(after) ? formatScheduleCompact(after) : null;

  if (!beforeSchedule && !afterSchedule) return null;

  return (
    <div className="flex gap-4 flex-wrap">
      <div className="flex-1 min-w-[140px]">
        <div className="text-[10px] uppercase text-red-600 font-medium mb-1">
          Avant
        </div>
        {beforeSchedule && beforeSchedule.length > 0 ? (
          <ScheduleCompactBlock data={beforeSchedule} />
        ) : (
          <span className="text-gray-400 text-[11px]">Aucun planning</span>
        )}
      </div>
      <div className="flex-1 min-w-[140px]">
        <div className="text-[10px] uppercase text-green-600 font-medium mb-1">
          Après
        </div>
        {afterSchedule && afterSchedule.length > 0 ? (
          <ScheduleCompactBlock data={afterSchedule} />
        ) : (
          <span className="text-gray-400 text-[11px]">Aucun planning</span>
        )}
      </div>
    </div>
  );
};

export { formatScheduleCompact, ScheduleCompactBlock, ScheduleChangeBeforeAfter };
